import { apiFetch, apiFetchBlob } from '../../../shared/api/apiFetch'

export interface TeamDocumentDto {
  id: string
  fileName: string
  storagePath: string
  isFolder: boolean
  uploaderId: string | null
  uploaderName: string | null
  updatedAt: string
}

export interface ListTeamDocumentsResponse {
  items: TeamDocumentDto[]
}

// Collection root keeps the trailing slash because the .NET endpoints register
// the list/create routes as `MapGet("/")` / `MapPost("/")` inside a route group
// (`/clubhouse/teams/{teamId}/documents`), and the integration tests in
// `apps/wehunt-web-api/WeHunt.Api.Tests/Modules/TeamDocuments/Api/TeamDocumentsEndpointsTests.cs`
// hit `/documents/` verbatim. Sub-paths (`/{itemId}`, `/batch-update`, `/batch`)
// are concatenated without an extra slash.
const base = (teamId: string) => `/clubhouse/teams/${encodeURIComponent(teamId)}/documents`

export async function listTeamDocuments(teamId: string, token: string): Promise<TeamDocumentDto[]> {
  const r = await apiFetch<ListTeamDocumentsResponse>(`${base(teamId)}/`, { token })
  return r.items
}

/**
 * Fetches a document's bytes through the API.
 *
 * `relPath` is the path below the team's own document root, exactly as the
 * document tree names it (e.g. `Kontaktlistor/Medlemmar 2026-27.xls`); the API
 * composes the storage path from it and the team in the route, so a caller
 * cannot address another team's documents with this team's membership.
 */
export async function downloadTeamDocument(
  teamId: string,
  relPath: string,
  token: string,
): Promise<Blob> {
  return apiFetchBlob(
    `${base(teamId)}/content?path=${encodeURIComponent(relPath)}`,
    { token },
  )
}

export async function createTeamDocument(
  teamId: string,
  body: {
    fileName: string
    storagePath: string
    isFolder: boolean
    uploaderId: string | null
    uploaderName: string | null
  },
  token: string,
): Promise<TeamDocumentDto> {
  return apiFetch<TeamDocumentDto>(`${base(teamId)}/`, {
    method: 'POST',
    body: JSON.stringify(body),
    token,
  })
}

export async function updateTeamDocument(
  teamId: string,
  itemId: string,
  body: { fileName: string; storagePath: string },
  token: string,
): Promise<TeamDocumentDto> {
  return apiFetch<TeamDocumentDto>(`${base(teamId)}/${encodeURIComponent(itemId)}`, {
    method: 'PUT',
    body: JSON.stringify(body),
    token,
  })
}

/**
 * Moves (or renames) a document or folder in Storage, on the server.
 *
 * Both paths are relative to the team's own document root, as `downloadTeamDocument`
 * takes them. A move cannot be done from the browser: copying an object means
 * reading its bytes, and the bucket's CORS policy blocks every byte read the
 * browser makes. The metadata rows are re-pointed afterwards with
 * `batchUpdateTeamDocuments`, as before.
 */
export async function moveTeamDocument(
  teamId: string,
  fromRelPath: string,
  toRelPath: string,
  isFolder: boolean,
  token: string,
): Promise<void> {
  await apiFetch<undefined>(`${base(teamId)}/move`, {
    method: 'POST',
    body: JSON.stringify({ from: fromRelPath, to: toRelPath, isFolder }),
    token,
  })
}

export async function batchUpdateTeamDocuments(
  teamId: string,
  items: Array<{ id: string; fileName: string; storagePath: string }>,
  token: string,
): Promise<TeamDocumentDto[]> {
  const r = await apiFetch<ListTeamDocumentsResponse>(`${base(teamId)}/batch-update`, {
    method: 'POST',
    body: JSON.stringify({ items }),
    token,
  })
  return r.items
}

export async function batchDeleteTeamDocuments(
  teamId: string,
  ids: string[],
  token: string,
): Promise<void> {
  await apiFetch<undefined>(`${base(teamId)}/batch`, {
    method: 'DELETE',
    body: JSON.stringify({ ids }),
    token,
  })
}
